import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

export default function TermsOfServicePage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-sans text-slate-800 bg-white">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#1AB790] hover:text-[#17a380] transition-colors mb-4"
          >
            ← Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Revisr Terms of Service
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Effective Date:</strong> 1/11/2025</p>
            <p><strong>Last Updated:</strong> 13/1/2026</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">1. About This Agreement</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of the Revisr.ai platform and services ("Service"), provided by Revisr Pty Ltd (ACN 693 866 447) trading as Revisr ('Revisr', 'we', 'us', or 'our').
            </p>
            <p className="text-gray-700 leading-relaxed">
              By creating an account, using our Service, or accessing Revisr.ai, you agree to these Terms and our Privacy Policy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If you do not agree, you may not use the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">2. Our Service</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr provides a cloud-based document compliance automation platform that helps businesses manage, audit, and update documentation.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The platform may include AI-driven tools and large language model (LLM) functionality to identify formatting issues, compliance gaps, or suggested text improvements.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We make every effort to ensure our outputs are accurate and up to date; however, AI systems can make mistakes. Revisr does not provide legal, financial, or compliance certification advice, and users are responsible for validating and approving all final content generated through the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">3. Who Can Use Revisr</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr is available to:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
              <li>Businesses, consultants, and professionals (B2B users); and</li>
              <li>Individual users (B2C) who accept these Terms personally.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old to create an account or have legal authority to bind your organisation to these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">4. Accounts and Access</h2>
            <p className="text-gray-700 leading-relaxed">
              You are responsible for all activity under your account and must keep your login credentials secure.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We may suspend or terminate your access if we detect misuse, unauthorised access, or breaches of these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any part of the Service with reasonable notice.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">5. Subscriptions and Payments</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr currently offers subscription-based access on monthly or annual plans.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Payments are processed securely via Stripe or similar providers.
            </p>
            <p className="text-gray-700 leading-relaxed">
              All fees are displayed in Australian dollars and are non-refundable except as required by law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We may introduce different plans or features over time — any changes will be reflected in updated Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">6. User Content and Ownership</h2>
            <p className="text-gray-700 leading-relaxed">
              You retain ownership of all files, documents, and materials ("User Content") that you upload to Revisr.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By uploading content, you grant Revisr a limited, revocable licence to process, analyse, and store it only as needed to provide the Service.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Revisr retains ownership of:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
              <li>The platform, software, algorithms, templates, and AI models it provides; and</li>
              <li>All intellectual property associated with the Service.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You must not copy, reverse-engineer, or redistribute any part of the platform without written permission.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">7. Data Storage and Security</h2>
            <p className="text-gray-700 leading-relaxed">
              Revisr takes data security seriously.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We host data using secure, access-controlled cloud infrastructure located primarily in Australia, with some optional services processed in other jurisdictions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We apply encryption, access controls, and regular audits to safeguard your data.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If a breach occurs that may cause serious harm, we will notify affected users and the Office of the Australian Information Commissioner (OAIC) in accordance with the Notifiable Data Breaches scheme.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">8. Acceptable Use</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
              <li>Use the Service for unlawful or misleading purposes.</li>
              <li>Upload, share, or distribute content that infringes third-party rights.</li>
              <li>Attempt to disrupt, bypass, or reverse-engineer any part of the Service.</li>
              <li>Use the platform to generate fraudulent or non-compliant documents.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Revisr may suspend accounts engaged in prohibited activity.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">9. Disclaimer and Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              While Revisr aims for accuracy and reliability, AI and LLM-generated outputs may contain errors or omissions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The Service is provided "as is" without warranties of any kind.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Revisr:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
              <li>Does not guarantee legal or regulatory compliance.</li>
              <li>Is not liable for any decisions, losses, or damages resulting from reliance on AI outputs or the platform's tools.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Revisr's total liability for any claim is limited to the amount you paid in the 12 months preceding the claim.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">10. Confidentiality</h2>
            <p className="text-gray-700 leading-relaxed">
              We treat all documents and data you upload as confidential and handle them in accordance with our Privacy Policy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We do not access, share, or use your documents for any purpose other than providing the Service, unless required by law.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">11. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              You may cancel your subscription or account at any time by emailing <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:text-[#17a380] underline">support@revisr.ai</a>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Revisr may suspend or terminate access if:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
              <li>You breach these Terms;</li>
              <li>Payment is not received; or</li>
              <li>Continued access poses a security or legal risk.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Upon termination, your data will be securely deleted in line with our data retention policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">12. Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these Terms as our Service evolves.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The latest version will always be available at <a href="https://www.revisr.ai/terms" className="text-[#1AB790] hover:text-[#17a380] underline">www.revisr.ai/terms</a> with a "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              If changes are significant, we'll notify active users by email or in-app notice.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">13. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of Victoria, Australia.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Any disputes will be resolved in the courts of Victoria.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-6 sm:mt-8 mb-4">14. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              Questions about these Terms can be sent to:
            </p>
            <p className="text-gray-700 leading-relaxed">
              📧 <a href="mailto:support@revisr.ai" className="text-[#1AB790] hover:text-[#17a380] underline">support@revisr.ai</a>
            </p>
            <p className="text-gray-700 leading-relaxed">
              ✉️ Revisr, Melbourne
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

